import {
  DataTableShell,
  tdClass,
  thClass,
} from "@/features/exports/components/DataTableShell";
import { InvoiceStatusBadge } from "@/features/invoices/components/InvoiceStatusBadge";
import { ClickCopyTextCell } from "@/features/invoices/components/ClickCopyTextCell";
import { CustomerEmailCell } from "@/features/invoices/components/CustomerEmailCell";
import { InvoiceIdCell } from "@/features/invoices/components/InvoiceIdCell";
import { InvoiceRowActionsCell } from "@/features/invoices/components/InvoiceRowActionsCell";
import type { RowDownloadKind } from "@/features/invoices/components/InvoiceRowDownloadCell";
import {
  formatInvoiceDateTime,
  formatMoney,
} from "@/domain/invoices/formatInvoice";
import {
  resolveCustomerEmail,
  resolveCustomerName,
} from "@/domain/customers/formatCustomer";
import { isZeroAmountInvoice } from "@/domain/invoices/receiptRow";
import type { CustomerRow, InvoiceRow } from "@/types/api";
import { cn } from "@/utils/cn";

export type InvoiceTableVariant = "pdfs" | "receipts";

export type HostedLinkActions = "none" | "open-only";

export type InvoiceRowDownloading = {
  invoiceId: string;
  kind: RowDownloadKind;
};

interface InvoiceTableProps {
  variant: InvoiceTableVariant;
  items: InvoiceRow[];
  customers: CustomerRow[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onRowDownload?: (row: InvoiceRow, kind: RowDownloadKind) => void;
  downloading?: InvoiceRowDownloading | null;
  embedded?: boolean;
  /** Show hosted invoice link actions (Open only on PDF page). */
  hostedLinkActions?: HostedLinkActions;
  /** Fixed column widths via colgroup — fills container evenly (PDF page trial). */
  fixedColumnLayout?: boolean;
  /** Hide row checkboxes (read-only preview tables). */
  showSelection?: boolean;
}

const compactThClass = `${thClass} px-1.5 py-1.5 invoice-cell`;
const compactTdClass = `${tdClass} px-1.5 py-2 invoice-cell`;
const tightThClass = `${compactThClass} col-tight`;
const tightTdClass = `${compactTdClass} col-tight`;
const invoiceIdThClass = `${tightThClass} col-resource-id col-invoice-id`;
const invoiceIdTdClass = `${tightTdClass} col-resource-id col-invoice-id`;
const fillThClass = `${compactThClass} col-fill`;
const fillTdClass = `${compactTdClass} col-fill`;
const downloadThClass = `${tightThClass} col-download`;
const downloadTdClass = `${tightTdClass} col-download`;
const actionThClass = `${compactThClass} col-actions`;
const actionTdClass = `${compactTdClass} col-actions`;

/** PDF download list: 11 columns. */
function InvoicePdfFixedColGroup() {
  return (
    <colgroup>
      <col className="invoice-col-check" />
      <col className="invoice-col-invoice" />
      <col className="invoice-col-customer" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-amount" />
      <col className="invoice-col-status" />
      <col className="invoice-col-date" />
      <col className="invoice-col-date" />
      <col className="invoice-col-download" />
      <col className="invoice-col-actions" />
    </colgroup>
  );
}

/** PDF preview (no checkbox column). */
function InvoicePdfPreviewColGroup() {
  return (
    <colgroup>
      <col className="invoice-col-invoice" />
      <col className="invoice-col-customer" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-amount" />
      <col className="invoice-col-status" />
      <col className="invoice-col-date" />
      <col className="invoice-col-date" />
      <col className="invoice-col-download" />
      <col className="invoice-col-actions" />
    </colgroup>
  );
}

/** Receipts download list: 10 columns (no Actions). */
function InvoiceReceiptsFixedColGroup() {
  return (
    <colgroup>
      <col className="invoice-col-check" />
      <col className="invoice-col-invoice" />
      <col className="invoice-col-customer" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-flex" />
      <col className="invoice-col-amount" />
      <col className="invoice-col-status" />
      <col className="invoice-col-date" />
      <col className="invoice-col-date" />
      <col className="invoice-col-download" />
    </colgroup>
  );
}

export function InvoiceTable({
  variant,
  items,
  customers,
  selected,
  onToggle,
  onRowDownload,
  downloading,
  embedded = false,
  hostedLinkActions = "none",
  fixedColumnLayout = false,
  showSelection = true,
}: InvoiceTableProps) {
  const isReceipts = variant === "receipts";
  const downloadLabel = isReceipts ? "Receipt" : "PDF";
  const showHostedLinkOpen = hostedLinkActions === "open-only";
  const useFixedLayout = fixedColumnLayout;

  const table = (
    <table
      className={cn(
        "invoice-table-compact w-full border-collapse text-left text-sm",
        embedded && !useFixedLayout && "min-w-[800px]",
        useFixedLayout && "invoice-table-compact--fixed-layout",
        useFixedLayout && showHostedLinkOpen && "invoice-table-compact--pdfs",
        useFixedLayout && isReceipts && "invoice-table-compact--receipts",
        useFixedLayout && !showSelection && "invoice-table-compact--no-selection",
      )}
    >
      {useFixedLayout && showHostedLinkOpen && showSelection ? (
        <InvoicePdfFixedColGroup />
      ) : null}
      {useFixedLayout && showHostedLinkOpen && !showSelection ? (
        <InvoicePdfPreviewColGroup />
      ) : null}
      {useFixedLayout && isReceipts ? <InvoiceReceiptsFixedColGroup /> : null}
      <thead>
        <tr>
          {showSelection ? <th className={`${tightThClass} w-8`} /> : null}
          <th className={invoiceIdThClass}>Invoice number</th>
          <th className={fillThClass}>Customer</th>
          <th className={tightThClass}>Customer ID</th>
          <th className={fillThClass}>Email</th>
          <th className={tightThClass}>Amount</th>
          <th className={tightThClass}>Status</th>
          <th className={`${tightThClass} col-date`}>Created</th>
          <th className={`${tightThClass} col-date`}>Due</th>
          <th className={downloadThClass}>{downloadLabel}</th>
          {showHostedLinkOpen ? (
            <th className={actionThClass}>Actions</th>
          ) : null}
        </tr>
      </thead>
      <tbody>
        {items.map((row) => (
          <InvoiceRowLine
            key={row.id}
            variant={variant}
            row={row}
            customers={customers}
            checked={selected.has(row.id)}
            disabled={row.exportable === false}
            onToggle={() => onToggle(row.id)}
            onRowDownload={onRowDownload}
            downloading={downloading}
            showHostedLinkOpen={showHostedLinkOpen}
            showSelection={showSelection}
          />
        ))}
      </tbody>
    </table>
  );

  return (
    <DataTableShell embedded={embedded}>
      {embedded ? table : <div className="overflow-x-auto">{table}</div>}
    </DataTableShell>
  );
}

function RowDownloadButton({
  label,
  disabled,
  loading,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  loading?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={cn("btn-row-dl btn-table-action", loading && "is-loading")}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      aria-label={loading ? `${label} downloading` : label}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {loading ? "..." : label}
    </button>
  );
}

function InvoiceRowLine({
  variant,
  row,
  customers,
  checked,
  disabled,
  onToggle,
  onRowDownload,
  downloading,
  showHostedLinkOpen,
  showSelection,
}: {
  variant: InvoiceTableVariant;
  row: InvoiceRow;
  customers: CustomerRow[];
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
  onRowDownload?: (row: InvoiceRow, kind: RowDownloadKind) => void;
  downloading?: InvoiceRowDownloading | null;
  showHostedLinkOpen: boolean;
  showSelection: boolean;
}) {
  const isReceipts = variant === "receipts";
  const zeroAmount = isZeroAmountInvoice(row);
  const canPdf = row.exportable !== false;
  const canReceipt =
    row.status === "paid" && row.exportable !== false && !zeroAmount;
  const kind = isReceipts ? "receipt" : "pdf";
  const canDownload = isReceipts ? canReceipt : canPdf;
  const checkboxDisabled =
    isReceipts && (disabled || zeroAmount) ? true : disabled;
  const name = resolveCustomerName(row, customers);
  const email = resolveCustomerEmail(row, customers);
  const rowDownloading =
    downloading?.invoiceId === row.id ? downloading.kind : null;
  return (
    <tr
      className={cn(
        disabled && !zeroAmount && "opacity-50",
        isReceipts && zeroAmount && "bg-[rgba(180,83,9,0.07)]",
      )}
    >
      {showSelection ? (
        <td className={`${tightTdClass} w-8`}>
          <input
            type="checkbox"
            checked={checked}
            disabled={checkboxDisabled}
            title={
              isReceipts && zeroAmount
                ? "No payment receipt for $0 paid invoices"
                : undefined
            }
            onChange={onToggle}
          />
        </td>
      ) : null}
      <td className={invoiceIdTdClass}>
        <InvoiceIdCell id={row.id} invoiceNumber={row.invoice_number} />
      </td>
      <td className={fillTdClass}>
        <ClickCopyTextCell
          value={name}
          resourceLabel="Customer name"
          variant="name"
        />
      </td>
      <td className={tightTdClass}>
        <ClickCopyTextCell
          value={row.customer_id}
          resourceLabel="Customer ID"
          variant="meta"
          tabularNums
        />
      </td>
      <td className={fillTdClass}>
        <CustomerEmailCell email={email} />
      </td>
      <td className={`${tightTdClass} tabular-nums text-sm`}>
        <span className="block truncate" title={formatMoney(row.amount_due, row.currency)}>
          {formatMoney(row.amount_due, row.currency)}
        </span>
      </td>
      <td className={tightTdClass}>
        <InvoiceStatusBadge status={row.status} />
      </td>
      <td className={`${tightTdClass} col-date cell-date tabular-nums`}>
        {formatInvoiceDateTime(row.created)}
      </td>
      <td className={`${tightTdClass} col-date cell-date tabular-nums`}>
        {row.due_date ? formatInvoiceDateTime(row.due_date) : "—"}
      </td>
      <td className={downloadTdClass}>
        {onRowDownload ? (
          <RowDownloadButton
            label="Download"
            disabled={!canDownload}
            loading={rowDownloading === kind}
            onClick={() => onRowDownload(row, kind)}
          />
        ) : (
          <span className="cell-meta">—</span>
        )}
      </td>
      {showHostedLinkOpen ? (
        <td className={actionTdClass}>
          <InvoiceRowActionsCell
            payLinkUrl={row.hosted_invoice_url}
            openOnly
          />
        </td>
      ) : null}
    </tr>
  );
}
